import React, { useState, useEffect, useRef, useCallback } from "react";
import { api } from "./api";
import { useAuth } from "./AuthContext";
import { Icon } from "./Icons";
import { VISTE, aggrega, fmt, labelData } from "./riepilogoUtils";

const C = {
  dark:     "#1A1A1A",
  blue:     "#1565C0",
  grey:     "#546E7A",
  light:    "#F5F5F5",
  border:   "#E0E0E0",
  text:     "#212121",
  textSub:  "#757575",
  error:    "#B71C1C",
  calcBg:   "#ECEFF1",   // grigio chiaro per le celle calcolate
};
const FONT = "Roboto, 'Segoe UI', Arial, sans-serif";

// Colonne della griglia. calc=true → cella calcolata (grigia, sola lettura).
// Le pro-capite sono   valore_numeratore / persone (campo `num`).
// `pr_prodotti` è calcolato con una formula custom (campo `formula`).
const COLS = [
  { key: "persone",               label: "Persone",                                calc: false },
  { key: "colli_totali_prodotti", label: "Colli Totali Prodotti",                  calc: false },
  { key: "pc_totali",             label: "Colli pro capite totali",                calc: true, num: "colli_totali_prodotti" },
  { key: "colli_carico_cnt",      label: "Colli prodotti per carico CNT Prog.",    calc: false },
  { key: "pc_carico",             label: "Colli pro capite per carico container",  calc: true, num: "colli_carico_cnt" },
  { key: "colli_senza_pr",        label: "Colli prodotti senza PR",                calc: false },
  { key: "pc_collo",              label: "Colli pro capite per collo completo",    calc: true, num: "colli_senza_pr" },
  { key: "pr_prodotti",           label: "Pr Prodotti",                            calc: true, formula: r => (r.colli_totali_prodotti || 0) - (r.colli_senza_pr || 0) },
];

// Valore di una colonna calcolata per una riga (giornaliera o aggregata).
// - `formula`: formula custom (es. Pr Prodotti).
// - `num`: pro-capite = valore / persone.
function calcValue(col, r) {
  if (col.formula) return col.formula(r);
  const persone = r.persone || 0;
  return persone > 0 ? r[col.num] / persone : null;
}

// ── Cella numerica editabile ──────────────────────────────────────────────────
function NumCell({ value, onChange, readOnly = false }) {
  const [local, setLocal] = useState(String(value ?? 0));
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setLocal(String(value ?? 0)); }, [value, editing]);
  return (
    <input
      type="number"
      value={local}
      readOnly={readOnly}
      onFocus={() => { if (!readOnly) setEditing(true); }}
      onChange={e => { if (!readOnly) setLocal(e.target.value); }}
      onBlur={() => {
        if (readOnly) return;
        setEditing(false);
        const v = parseFloat(local) || 0;
        if (v !== (value ?? 0)) onChange(v);
      }}
      onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }}
      style={{
        width: "100%", boxSizing: "border-box", border: "none", background: "transparent",
        textAlign: "center", fontSize: 15, fontWeight: 600, padding: "8px 6px",
        outline: "none", fontFamily: FONT, color: C.text,
        cursor: readOnly ? "default" : "text",
      }}
    />
  );
}

// ── Componente principale ─────────────────────────────────────────────────────
export default function Riepilogo() {
  const { canEdit } = useAuth();
  const puoModificare = canEdit("riepilogo");
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [nuovaData, setNuovaData] = useState("");
  const [vista, setVista]     = useState("giornaliera");
  const saveTimer = useRef({});
  const rowsRef = useRef([]);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  useEffect(() => { rowsRef.current = rows; }, [rows]);

  // Import CSV colli
  const [importData, setImportData]       = useState(null);   // risposta preview { giorni, avvisi, ... }
  const [importInputs, setImportInputs]   = useState({});     // { dataISO: { persone, carico } }
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError]     = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.getRiepilogo();
      setRows(d);
      setError("");
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (data, key, value) => {
    // aggiornamento ottimistico
    setRows(prev => prev.map(r => r.data === data ? { ...r, [key]: value } : r));
    // salvataggio debounced dell'intera riga (legge lo stato aggiornato via rowsRef)
    if (saveTimer.current[data]) clearTimeout(saveTimer.current[data]);
    saveTimer.current[data] = setTimeout(async () => {
      const riga = rowsRef.current.find(r => r.data === data) || {};
      try {
        await api.upsertRiepilogo({
          data,
          persone:               riga.persone ?? 0,
          colli_totali_prodotti: riga.colli_totali_prodotti ?? 0,
          colli_carico_cnt:      riga.colli_carico_cnt ?? 0,
          colli_senza_pr:        riga.colli_senza_pr ?? 0,
          // pr_prodotti è ora derivato (colli_totali_prodotti - colli_senza_pr): inviato per compatibilità backend
          pr_prodotti:           (riga.colli_totali_prodotti ?? 0) - (riga.colli_senza_pr ?? 0),
        });
        setError("");
      } catch (e) {
        setError("Errore salvataggio: " + e.message);
        load();
      }
    }, 400);
  };

  const handleAggiungi = async () => {
    if (!nuovaData) return;
    try {
      await api.aggiungiDataRiepilogo(nuovaData);
      setNuovaData("");
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const handleElimina = async (data) => {
    if (!window.confirm(`Rimuovere il giorno "${labelData(data)}" dalla griglia?`)) return;
    try {
      await api.eliminaDataRiepilogo(data);
      setRows(prev => prev.filter(r => r.data !== data));
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Import CSV ────────────────────────────────────────────────────────────────
  const handleCaricaFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setImportLoading(true);
    setImportError("");
    setImportData({ giorni: [], avvisi: [] });   // apre la scheda in stato "lettura"
    try {
      const res = await api.importRiepilogoCsvPreview(files);
      const inputs = {};
      (res.giorni || []).forEach(g => {
        inputs[g.data] = { persone: g.persone_attuale || 0, carico: g.colli_carico_cnt_attuale || 0 };
      });
      setImportInputs(inputs);
      setImportData(res);
    } catch (e) {
      setImportError(e.message);
    } finally {
      setImportLoading(false);
    }
  };

  const setImportInput = (dataISO, campo, valore) => {
    setImportInputs(prev => ({ ...prev, [dataISO]: { ...prev[dataISO], [campo]: valore } }));
  };

  const handleConfermaImport = async () => {
    if (!importData?.giorni?.length) return;
    setImportLoading(true);
    try {
      for (const g of importData.giorni) {
        const inp = importInputs[g.data] || {};
        await api.upsertRiepilogo({
          data:                  g.data,
          persone:               parseFloat(inp.persone) || 0,
          colli_totali_prodotti: g.colli_totali_prodotti,
          colli_carico_cnt:      parseFloat(inp.carico) || 0,
          colli_senza_pr:        g.colli_senza_pr,
          pr_prodotti:           g.pr_prodotti,
        });
      }
      setImportData(null);
      setImportInputs({});
      setImportError("");
      setVista("giornaliera");
      await load();
    } catch (e) {
      setImportError("Errore in fase di caricamento: " + e.message);
    } finally {
      setImportLoading(false);
    }
  };

  const chiudiImport = () => { setImportData(null); setImportInputs({}); setImportError(""); };

  const thBase = {
    background: C.grey, color: "#fff", fontWeight: 700, fontSize: 13,
    padding: "10px 8px", textAlign: "center", borderLeft: "1px solid rgba(255,255,255,0.15)",
    position: "sticky", top: 0, zIndex: 3,
  };
  const tdBase = { borderBottom: `1px solid ${C.border}`, borderLeft: `1px solid ${C.border}`, height: 36 };

  const isDaily = vista === "giornaliera";
  const displayRows = isDaily ? rows : aggrega(rows, vista);

  // La tabella si apre sull'ultima riga (giorno più recente in fondo).
  useEffect(() => {
    if (loading) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [loading, isDaily, displayRows.length]);

  return (
    <div style={{ fontFamily: FONT, background: C.light, minHeight: "100vh", padding: 20 }}>

      {/* Toolbar: switch vista */}
      <div style={{
        background: "#fff", borderRadius: 8, padding: "10px 16px", marginBottom: 12,
        border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.6, marginRight: 6 }}>
          Vista
        </span>
        {VISTE.map(v => (
          <button
            key={v.id}
            onClick={() => setVista(v.id)}
            style={{
              background: vista === v.id ? C.blue : "#fff",
              color: vista === v.id ? "#fff" : C.grey,
              border: `1.5px solid ${vista === v.id ? C.blue : C.border}`, borderRadius: 6,
              padding: "7px 16px", cursor: "pointer", fontFamily: FONT, fontWeight: 700, fontSize: 14,
            }}
          >
            {v.label}
          </button>
        ))}
        <span style={{ marginLeft: "auto", fontSize: 13, color: C.textSub }}>
          {isDaily
            ? <>celle <span style={{ background: C.calcBg, padding: "1px 6px", borderRadius: 3 }}>grigie</span> = calcolate</>
            : <>{displayRows.length} periodi · media dei valori (sola lettura)</>}
        </span>
      </div>

      {/* Toolbar: aggiungi data (solo vista giornaliera, solo chi può modificare) */}
      {isDaily && puoModificare && (
      <div style={{
        background: "#fff", borderRadius: 8, padding: "12px 16px", marginBottom: 14,
        border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.6 }}>
          Aggiungi giorno
        </span>
        <input
          type="date"
          value={nuovaData}
          onChange={e => setNuovaData(e.target.value)}
          style={{ border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "7px 10px", fontSize: 14, fontFamily: FONT, outline: "none" }}
        />
        <button
          onClick={handleAggiungi}
          disabled={!nuovaData}
          style={{
            background: nuovaData ? C.blue : "#B0BEC5", color: "#fff", border: "none", borderRadius: 6,
            padding: "8px 18px", cursor: nuovaData ? "pointer" : "default", fontFamily: FONT, fontWeight: 700, fontSize: 14,
          }}
        >
          + Aggiungi
        </button>

        <div style={{ width: 1, height: 26, background: C.border }} />
        <input ref={fileRef} type="file" accept=".csv" multiple style={{ display: "none" }}
          onChange={e => { handleCaricaFiles(e.target.files); e.target.value = ""; }} />
        <button
          onClick={() => fileRef.current?.click()}
          title="Importa uno o più CSV di dettaglio colli"
          style={{
            background: C.dark, color: "#fff", border: "none", borderRadius: 6,
            padding: "8px 16px", cursor: "pointer", fontFamily: FONT, fontWeight: 700, fontSize: 14,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >
          <Icon name="upload" size={16} color="#fff" /> Carica dati
        </button>

        <span style={{ marginLeft: "auto", fontSize: 13, color: C.textSub }}>
          {rows.length} giorni
        </span>
      </div>
      )}

      {error && (
        <div style={{
          background: "#FFEBEE", border: "1px solid #EF9A9A", color: C.error,
          padding: "10px 14px", borderRadius: 6, marginBottom: 12, fontWeight: 600,
          display: "flex", alignItems: "center",
        }}>
          <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{error}
          <button onClick={() => setError("")} style={{ marginLeft: "auto", cursor: "pointer", background: "none", border: "none", fontWeight: 700, fontSize: 18, color: C.error }}>×</button>
        </div>
      )}

      {loading ? (
        <div style={{ padding: 60, textAlign: "center", color: C.textSub }}>Caricamento…</div>
      ) : (
        <div ref={scrollRef} style={{
          background: "#fff", borderRadius: 8, border: `1px solid ${C.border}`, overflow: "auto",
          maxHeight: "calc(100vh - var(--app-header-h, 140px) - 100px)",
          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
        }}>
          <table style={{ borderCollapse: "collapse", width: "100%", fontFamily: FONT }}>
            <thead>
              <tr>
                <th style={{
                  ...thBase, textAlign: "left", paddingLeft: 14, minWidth: 170,
                  position: "sticky", left: 0, top: 0, zIndex: 4, borderLeft: "none",
                }}>
                  Data
                </th>
                {COLS.map(col => (
                  <th key={col.key} style={{ ...thBase, minWidth: 110, background: col.calc ? "#78909C" : C.grey }}>
                    {col.label}
                  </th>
                ))}
                <th style={{ ...thBase, minWidth: 50 }}></th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r, i) => {
                const zebra = i % 2 === 0 ? "#fff" : "#FAFAFA";
                const rowKey = isDaily ? r.data : r.key;
                const rowLabel = isDaily ? labelData(r.data) : r.label;
                return (
                  <tr key={rowKey}>
                    <td style={{
                      ...tdBase, fontWeight: 600, color: C.grey, fontSize: 13, paddingLeft: 14,
                      position: "sticky", left: 0, zIndex: 2, background: zebra, borderLeft: "none",
                    }}>
                      {rowLabel}
                    </td>
                    {COLS.map(col => {
                      if (col.calc) {
                        return (
                          <td key={col.key} style={{ ...tdBase, background: C.calcBg, textAlign: "center", fontWeight: 600, fontSize: 15, color: C.text }}>
                            {fmt(calcValue(col, r))}
                          </td>
                        );
                      }
                      // Viste aggregate: sola lettura (media dei valori)
                      if (!isDaily) {
                        return (
                          <td key={col.key} style={{ ...tdBase, textAlign: "center", fontWeight: 600, fontSize: 15, color: C.text, background: zebra }}>
                            {fmt(r[col.key])}
                          </td>
                        );
                      }
                      return (
                        <td key={col.key} style={{ ...tdBase, padding: 0, background: zebra }}>
                          <NumCell value={r[col.key]} onChange={v => handleChange(r.data, col.key, v)} readOnly={!puoModificare} />
                        </td>
                      );
                    })}
                    <td style={{ ...tdBase, textAlign: "center", background: zebra }}>
                      {isDaily && puoModificare && (
                        <button
                          onClick={() => handleElimina(r.data)}
                          title="Rimuovi questo giorno"
                          style={{ background: "none", border: "none", cursor: "pointer", color: C.error, fontSize: 16, fontWeight: 700, lineHeight: 1 }}
                        >
                          ×
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Scheda import CSV ─────────────────────────────────────────────── */}
      {importData && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 640, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", fontFamily: FONT }}>
            <div style={{ background: C.dark, color: "#fff", padding: "14px 20px", borderRadius: "10px 10px 0 0", fontWeight: 700, fontSize: 16 }}>
              Carica dati — Riepilogo
            </div>
            <div style={{ padding: 20, overflowY: "auto" }}>
              {importLoading && importData.giorni.length === 0 && (
                <div style={{ padding: 20, textAlign: "center", color: C.textSub }}>Lettura file…</div>
              )}
              {importError && (
                <div style={{ background: "#FFEBEE", border: "1px solid #EF9A9A", color: C.error, padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontWeight: 600, display: "flex", alignItems: "center" }}>
                  <Icon name="warning" size={16} color={C.error} style={{ marginRight: 6 }} />{importError}
                </div>
              )}
              {importData.avvisi && importData.avvisi.length > 0 && (
                <div style={{ background: "#FFF8E1", border: "1px solid #F57F17", color: "#8a6d00", padding: "10px 14px", borderRadius: 6, marginBottom: 14, fontSize: 13 }}>
                  {importData.avvisi.join(" · ")}
                </div>
              )}
              {importData.giorni.map(g => {
                const inp = importInputs[g.data] || {};
                return (
                  <div key={g.data} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 16, marginBottom: 14 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
                      {labelData(g.data)}
                      {g.esiste && <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: C.error }}>· giorno già presente: verrà sovrascritto</span>}
                    </div>
                    <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 14, fontSize: 14, color: C.text }}>
                      <div>Colli totali prodotti: <b>{g.colli_totali_prodotti.toLocaleString("it-IT")}</b></div>
                      <div>Colli prodotti senza PR: <b>{g.colli_senza_pr.toLocaleString("it-IT")}</b></div>
                      <div style={{ color: C.textSub }}>PR: {g.pr_prodotti.toLocaleString("it-IT")}</div>
                      <div style={{ color: C.textSub }}>{g.n_righe} righe</div>
                    </div>
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Persone
                        <input type="number" value={inp.persone ?? 0} onChange={e => setImportInput(g.data, "persone", e.target.value)}
                          style={{ border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "8px 10px", fontSize: 15, width: 140, fontFamily: FONT, outline: "none" }} />
                      </label>
                      <label style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, fontWeight: 700, color: C.textSub, textTransform: "uppercase", letterSpacing: 0.5 }}>
                        Colli prodotti per carico CNT Prog.
                        <input type="number" value={inp.carico ?? 0} onChange={e => setImportInput(g.data, "carico", e.target.value)}
                          style={{ border: `1.5px solid ${C.blue}`, borderRadius: 6, padding: "8px 10px", fontSize: 15, width: 220, fontFamily: FONT, outline: "none" }} />
                      </label>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ padding: "14px 20px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={chiudiImport} disabled={importLoading}
                style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 18px", cursor: "pointer", fontFamily: FONT, color: C.textSub, fontWeight: 600 }}>
                Annulla
              </button>
              <button onClick={handleConfermaImport} disabled={importLoading || importData.giorni.length === 0}
                style={{ background: C.blue, color: "#fff", border: "none", borderRadius: 6, padding: "9px 20px", cursor: (importLoading || !importData.giorni.length) ? "default" : "pointer", fontFamily: FONT, fontWeight: 700, opacity: (importLoading || !importData.giorni.length) ? 0.6 : 1 }}>
                {importLoading ? "Carico…" : "Conferma e carica"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
